/**
 * South African ID Number Utilities
 *
 * ID Number Format: YYMMDDGSSSCAZ
 * - YY: Year of birth (00-99)
 * - MM: Month (01-12)
 * - DD: Day (01-31)
 * - G: Gender (0-4 female, 5-9 male)
 * - SSS: Sequence number (000-999)
 * - C: Citizenship (0 = SA citizen, 1 = permanent resident)
 * - A: Usually 8 (former race classification, pre-1980)
 * - Z: Checksum digit
 *
 * CRITICAL: ID numbers starting with 0 (born 2000-2009) must preserve the leading zero!
 */

/**
 * Ensures ID number is stored and retrieved as a proper 13-character string
 * @param {string|number} idNumber - The ID number to normalize
 * @returns {string} - Properly formatted 13-character ID number string
 */
export const normalizeIdNumber = (idNumber) => {
  if (!idNumber) return '';

  // Convert to string and remove any spaces/dashes
  let cleaned = String(idNumber).replace(/[\s-]/g, '');

  // If it's exactly 12 digits and doesn't start with 0, it might be missing the leading zero
  if (cleaned.length === 12 && /^\d{12}$/.test(cleaned)) {
    // Check if this could be a valid ID starting with 0
    const firstTwoDigits = parseInt(cleaned.substring(0, 2), 10);

    // If year is 0-30, assume it's 2000-2030 and needs leading zero
    if (firstTwoDigits >= 0 && firstTwoDigits <= 30) {
      cleaned = '0' + cleaned;
    }
  }

  // Pad with leading zeros if needed (should rarely happen)
  while (cleaned.length < 13 && /^\d+$/.test(cleaned)) {
    cleaned = '0' + cleaned;
  }

  return cleaned;
};

/**
 * Validates South African ID number format and checksum
 * @param {string|number} idNumber - The ID number to validate
 * @returns {Object} - { valid: boolean, error: string, normalized: string }
 */
export const validateIdNumber = (idNumber) => {
  const normalized = normalizeIdNumber(idNumber);

  // Check if it's exactly 13 digits
  if (!/^\d{13}$/.test(normalized)) {
    return {
      valid: false,
      error: 'ID number must be exactly 13 digits',
      normalized: normalized
    };
  }

  // Basic date validation (YYMMDD)
  const month = parseInt(normalized.substring(2, 4), 10);
  const day = parseInt(normalized.substring(4, 6), 10);

  if (month < 1 || month > 12) {
    return {
      valid: false,
      error: 'Invalid month in ID number',
      normalized: normalized
    };
  }

  if (day < 1 || day > 31) {
    return {
      valid: false,
      error: 'Invalid day in ID number',
      normalized: normalized
    };
  }

  // Luhn algorithm checksum validation
  let sum = 0;
  let shouldDouble = false;

  for (let i = normalized.length - 1; i >= 0; i--) {
    let digit = parseInt(normalized.charAt(i), 10);

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  if (sum % 10 !== 0) {
    return {
      valid: false,
      error: 'Invalid ID number checksum',
      normalized: normalized
    };
  }

  return {
    valid: true,
    error: null,
    normalized: normalized
  };
};

/**
 * Parses gender from ID number
 * @param {string|number} idNumber - The ID number
 * @returns {string} - 'Male', 'Female', or empty string
 */
export const parseGenderFromId = (idNumber) => {
  const normalized = normalizeIdNumber(idNumber);
  if (!normalized || normalized.length < 7) return '';

  const genderDigit = parseInt(normalized.substring(6, 7), 10);
  return genderDigit >= 0 && genderDigit <= 4 ? 'Female' : 'Male';
};

/**
 * Parses date of birth from ID number
 * @param {string|number} idNumber - The ID number
 * @returns {string} - Date in YYYY-MM-DD format, or empty string
 */
export const parseDateOfBirthFromId = (idNumber) => {
  const normalized = normalizeIdNumber(idNumber);
  if (!normalized || normalized.length < 6) return '';

  const year = parseInt(normalized.substring(0, 2), 10);
  const month = normalized.substring(2, 4);
  const day = normalized.substring(4, 6);

  // Years 00-30 are 2000-2030, 31-99 are 1931-1999
  const fullYear = year <= 30 ? 2000 + year : 1900 + year;
  return `${fullYear}-${month}-${day}`;
};

/**
 * Formats ID number for display with spaces for readability
 * @param {string|number} idNumber - The ID number
 * @returns {string} - Formatted as YYMMDD GSSS CAZ
 */
export const formatIdNumberForDisplay = (idNumber) => {
  const normalized = normalizeIdNumber(idNumber);
  if (!normalized || normalized.length !== 13) return normalized;

  // Format: YYMMDD GSSS CAZ
  return `${normalized.substring(0, 6)} ${normalized.substring(6, 10)} ${normalized.substring(10, 13)}`;
};

/**
 * Checks if an ID number belongs to someone born after 1999 (starts with 0)
 * @param {string|number} idNumber - The ID number
 * @returns {boolean} - True if born 2000-2009
 */
export const isBornAfter1999 = (idNumber) => {
  const normalized = normalizeIdNumber(idNumber);
  if (!normalized || normalized.length < 2) return false;

  const year = parseInt(normalized.substring(0, 2), 10);
  return year >= 0 && year <= 9; // 2000-2009
};

/**
 * Safely stores ID number to Firestore (ensures it's a string)
 * @param {string|number} idNumber - The ID number
 * @returns {string} - ID number guaranteed to be stored as string
 */
export const prepareIdNumberForFirestore = (idNumber) => {
  const normalized = normalizeIdNumber(idNumber);

  // Explicitly return as string, even if it looks like a number
  // This prevents Firestore from auto-converting to number
  return String(normalized);
};

const idNumberUtils = {
  normalizeIdNumber,
  validateIdNumber,
  parseGenderFromId,
  parseDateOfBirthFromId,
  formatIdNumberForDisplay,
  isBornAfter1999,
  prepareIdNumberForFirestore
};
export default idNumberUtils;
